import { useState, useEffect, useRef } from "react";
import WeVillageLogo from "../images/wevillage_logo_main.webp";
import WeBabyVillageLogo from "../images/babyVillage_logo.webp";
import {
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
  Checkbox,
  FormGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DatePicker from "react-multi-date-picker";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { toast } from "react-toastify";
import axios from "axios";

const BookingForm = ({ service, type, promo }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const stripe = useStripe();
  const elements = useElements();
  const debounceTimer = useRef();

  // ---------------------
  // 🟦 NEW STATES FOR PROMO LOGIC
  // ---------------------
  const [promoAlreadyUsed, setPromoAlreadyUsed] = useState(false);
  const [forceRemoveDiscount, setForceRemoveDiscount] = useState(false);

  // COUPON CODE STATE
  const [couponCode, setCouponCode] = useState("");
  const [couponError, setCouponError] = useState("");
  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [couponUsedByExistingUser, setCouponUsedByExistingUser] = useState(false);

  // URL promo
  const urlHasPromo = promo === "off50";
  const urlDiscount = urlHasPromo ? 50 : 0;

  // Final discount amount
  const discountPercent = forceRemoveDiscount ? 0 : urlDiscount;

  const costTableShermanOaks = {
    "6 WEEKS - 2 YEARS": { full: 175, half: 95 },
    "2 - 3 YEARS": { full: 150, half: 85 },
    "3 - 12 YEARS": { full: 130, half: 75 },
  };

  const costTablePortland = {
    "6 WEEKS - 2 YEARS": { full: 165, half: 95 },
    "2 - 3 YEARS": { full: 150, half: 85 },
    "3 - 5 YEARS": { full: 130, half: 75 },
  };

  const costTable =
    type?.toLowerCase() === "portland"
      ? costTablePortland
      : costTableShermanOaks;

  const [parentName, setParentName] = useState("");
  const [parentContact, setParentContact] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [childName, setChildName] = useState("");
  const [childAge, setChildAge] = useState("");
  const [address, setAddress] = useState("");
  const [ageGroup, setAgeGroup] = useState(service || "6 WEEKS - 2 YEARS");
  const [dayType, setDayType] = useState("Single Day");
  const [dayDuration, setDayDuration] = useState("Full Day");
  const [singleDay, setSingleDay] = useState(null);
  const [multipleDays, setMultipleDays] = useState([]);
  const [includeLunch, setIncludeLunch] = useState(false);
  const [includeLatePickup, setIncludeLatePickup] = useState(false);
  const [calculatedCost, setCalculatedCost] = useState(0);
  const [originalCost, setOriginalCost] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isExisting, setIsExisting] = useState(false);
  const [showExtraFeeDialog, setShowExtraFeeDialog] = useState(false);

  // ---------------------
  // 📞 Phone formatting
  // ---------------------
  const handleContactChange = (e) => {
    let digits = e.target.value.replace(/\D/g, "");
    if (digits.length > 10) digits = digits.slice(0, 10);
    let formatted = digits;
    if (digits.length > 6) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
        6
      )}`;
    } else if (digits.length > 3) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else if (digits.length > 0) {
      formatted = `(${digits}`;
    }
    setParentContact(formatted);
  };

  // --------------------------------------------------
  // 🔍 EMAIL CHECK (returns exists + promoApplied)
  // --------------------------------------------------
  useEffect(() => {
    const checkExisting = async () => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!parentEmail || !emailRegex.test(parentEmail)) return;

      try {
        const res = await axios.post(
          "https://we-village-api.onrender.com/api/booking/check-existing",
          { parentEmail }
        );

        const exists = res?.data?.exists;
        const promoUsed = res?.data?.promoApplied;
        // Check if coupon was applied before
        const couponUsed = res?.data?.couponApplied; // Expecting boolean from API

        // Existing parent logic
        if (exists) {
          setIsExisting(true);
          // showExtraFeeDialog is removed as per requirements
        } else {
          setIsExisting(false);
        }

        // ----------------------------------------
        // 🟦 PROMO ALREADY USED LOGIC
        // ----------------------------------------
        if (urlHasPromo && promoUsed === true) {
          setPromoAlreadyUsed(true);
          setForceRemoveDiscount(true);
        } else {
          setPromoAlreadyUsed(false);
          setForceRemoveDiscount(false);
        }

        // ----------------------------------------
        // 🎟️ COUPON ALREADY USED LOGIC
        // ----------------------------------------
        if (couponUsed === true) {
          setCouponUsedByExistingUser(true);
          // If they typed something valid before check returned, invalidate it visually
          setIsCouponApplied(false);
        } else {
          setCouponUsedByExistingUser(false);
        }

      } catch (err) {
        console.error("Email check failed:", err);
      }
    };

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(checkExisting, 600);

    return () => clearTimeout(debounceTimer.current);
  }, [parentEmail]);

  // ---------------------
  // 💲 COST CALCULATION
  // ---------------------
  useEffect(() => {
    let cost = 0;

    if (dayType === "Single Day") {
      cost =
        dayDuration === "Full Day"
          ? costTable[ageGroup].full
          : costTable[ageGroup].half;
    } else if (dayType === "Multiple Days" && multipleDays.length > 0) {
      cost = multipleDays.length * costTable[ageGroup].full;
    }

    if (includeLunch) {
      cost += dayType === "Single Day" ? 10 : multipleDays.length * 10;
    }

    if (includeLatePickup && type?.toLowerCase() === "sherman-oaks") cost += 15;

    setOriginalCost(cost);

    let finalCost = cost;

    // apply discount
    if (discountPercent > 0) {
      finalCost = finalCost - (finalCost * discountPercent) / 100;
    }

    // apply coupon discount ($50 flat off)
    // Only if:
    // 1. Coupon is valid/applied
    // 2. Coupon is NOT used by existing user (double check)
    // 3. NO promo code applied (requirement: "visible only of no promo code is applied")
    if (isCouponApplied && !couponUsedByExistingUser && !urlHasPromo) {
      finalCost = finalCost - 50;
    }

    // Ensure cost doesn't go below 0
    if (finalCost < 0) finalCost = 0;

    setCalculatedCost(finalCost);
  }, [
    ageGroup,
    dayType,
    dayDuration,
    multipleDays,
    includeLunch,
    includeLatePickup,
    isExisting,
    isExisting,
    discountPercent,
    isCouponApplied, // dependency
    couponUsedByExistingUser, // dependency
  ]);

  // ---------------------
  // 🎟️ COUPON HANDLER
  // ---------------------
  const handleCouponBlur = () => {
    if (!couponCode) {
      setCouponError("");
      setIsCouponApplied(false);
      return;
    }

    if (couponCode === "WEVILLAGE50") {
      setCouponError("");
      setIsCouponApplied(true);
    } else {
      setCouponError("Invalid Coupon Code");
      setIsCouponApplied(false);
    }
  };

  // ---------------------
  // 📅 Calendar Date Disabling Logic
  // ---------------------
  const disabledDates = [
    "2025-12-24",
    "2025-12-25",
    "2026-01-01",
  ];

  const mapDays = ({ date }) => {
    const dateStr = date.format("YYYY-MM-DD");
    if (disabledDates.includes(dateStr)) {
      return {
        disabled: true,
        style: { color: "#ccc", textDecoration: "line-through" },
      };
    }
  };

  // ---------------------
  // FORM validation
  // ---------------------
  const validateForm = () => {
    if (
      !parentName ||
      !parentContact ||
      !parentEmail ||
      !childName ||
      !childAge ||
      !address ||
      (dayType === "Single Day" && !singleDay) ||
      (dayType === "Multiple Days" && multipleDays.length === 0)
    ) {
      toast.error("Please fill all required fields before booking.");
      return false;
    }
    return true;
  };

  useEffect(() => {
    setAgeGroup(service || "6 WEEKS - 2 YEARS");
  }, [service, type]);

  // --------------------------------------------------
  // 🧾 BOOK & PAY
  // --------------------------------------------------
  const handleBookAndPay = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (!stripe || !elements) {
      toast.error("Stripe has not loaded yet. Please try again.");
      return;
    }

    setLoading(true);

    try {
      const card = elements.getElement(CardElement);
      const { error, token } = await stripe.createToken(card);

      if (error) {
        toast.error(error.message);
        setLoading(false);
        return;
      }

      const payload = {
        parentName,
        parentNumber: parentContact,
        parentEmail,
        childName,
        childAge,
        address,
        bookingDate:
          dayType === "Single Day" && singleDay
            ? singleDay.format("YYYY-MM-DD")
            : "",
        dayType,
        dayDuration: dayType === "Single Day" ? dayDuration : "Full Day",
        multipleDates:
          dayType === "Multiple Days"
            ? multipleDays.map((d) => d.format("YYYY-MM-DD"))
            : [],
        ageGroup,
        includeLunch,
        includeLatePickup,
        price: calculatedCost,

        // only send promoApplied if discount actually applied
        promoApplied: urlHasPromo && !forceRemoveDiscount,

        // Coupon logic
        couponApplied: isCouponApplied && !couponUsedByExistingUser && !urlHasPromo,
        couponAmount: (isCouponApplied && !couponUsedByExistingUser && !urlHasPromo) ? 50 : 0,

        card_data: token,
        existingParentFee: isExisting,
      };

      const response = await axios.post(
        "https://we-village-api.onrender.com/api/booking/create",
        payload
      );

      if (response.status === 200 || response.status === 201) {
        toast.success("Booking submitted & payment processed!");
        setTimeout(() => {
          if (type?.toLowerCase() === "portland") {
            window.location.href =
              "https://webabyvillage.com/flexcare-booking-thank-you/";
          } else {
            window.location.href =
              "https://wevillage.com/flexcare-booking-thank-you/";
          }
        }, 1000);
      } else {
        toast.error("Booking failed. Please try again.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contentOuter">
      <div className="contentInner">
        {discountPercent > 0 && (
          <div className="ribbon">
            <span>🎉 {discountPercent}% OFF!</span>
          </div>
        )}

        <div className="wLogo mb-4">
          <img
            src={
              type?.toLowerCase() === "portland"
                ? WeBabyVillageLogo
                : WeVillageLogo
            }
            alt="weVillage_logo"
          />
          <h1>Flexcare Booking</h1>
          <h6
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "end",
              gap: "8px",
            }}
          >
            Amount to Pay:
            {originalCost > calculatedCost ? (
              <>
                <span style={{ textDecoration: "line-through", color: "#888" }}>
                  ${originalCost}
                </span>
                <span style={{ fontWeight: "bold", color: "#2E7D32" }}>
                  ${calculatedCost}
                </span>
              </>
            ) : (
              <span>${calculatedCost}</span>
            )}
          </h6>
        </div>

        {/* FORM */}
        <form onSubmit={handleBookAndPay}>
          <p className="guidance">Fields with (*) are required.</p>

          {/* fields ... unchanged */}
          {/* Keeping your original JSX same */}

          <div className="row g-3">
            <div className="col-sm-12">
              <TextField
                label="Parent Name"
                value={parentName}
                onChange={(e) => setParentName(e.target.value)}
                required
                size={isMobile ? "small" : "medium"}
                fullWidth
              />
            </div>

            <div className="col-sm-6">
              <TextField
                label="Parent Contact Number"
                value={parentContact}
                onChange={handleContactChange}
                required
                size={isMobile ? "small" : "medium"}
                fullWidth
                type="tel"
                placeholder="(123) 456-7890"
              />
            </div>

            <div className="col-sm-6">
              <TextField
                label="Parent Email"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
                required
                size={isMobile ? "small" : "medium"}
                fullWidth
                type="email"
              />
            </div>

            <div className="col-sm-6">
              <TextField
                label="Child Name"
                value={childName}
                onChange={(e) => setChildName(e.target.value)}
                required
                size={isMobile ? "small" : "medium"}
                fullWidth
              />
            </div>

            <div className="col-sm-6">
              <TextField
                label="Child Age"
                value={childAge}
                onChange={(e) => setChildAge(e.target.value)}
                required
                size={isMobile ? "small" : "medium"}
                fullWidth
                type="number"
                inputProps={{ min: 0 }}
              />
            </div>



            <div className="col-sm-12">
              <TextField
                label="Address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                required
                size={isMobile ? "small" : "medium"}
                fullWidth
                multiline
                rows={3}
              />
            </div>

            {/* Age Group */}
            <div className="col-sm-6">
              {type?.toLowerCase() === "portland" ? (
                <FormControl fullWidth>
                  <InputLabel>Age Group</InputLabel>
                  <Select
                    value={ageGroup}
                    label="Age Group"
                    size={isMobile ? "small" : "medium"}
                    onChange={(e) => setAgeGroup(e.target.value)}
                  >
                    <MenuItem value="6 WEEKS - 2 YEARS">
                      6 Weeks - 2 Years
                    </MenuItem>
                    <MenuItem value="2 - 3 YEARS">2 - 3 Years</MenuItem>
                    <MenuItem value="3 - 5 YEARS">3 - 5 Years</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <FormControl fullWidth>
                  <InputLabel>Age Group</InputLabel>
                  <Select
                    value={ageGroup}
                    label="Age Group"
                    size={isMobile ? "small" : "medium"}
                    onChange={(e) => setAgeGroup(e.target.value)}
                  >
                    <MenuItem value="6 WEEKS - 2 YEARS">
                      6 Weeks - 2 Years
                    </MenuItem>
                    <MenuItem value="2 - 3 YEARS">2 - 3 Years</MenuItem>
                    <MenuItem value="3 - 12 YEARS">3 - 12 Years</MenuItem>
                  </Select>
                </FormControl>
              )}
            </div>

            {/* Day Type */}
            <div className="col-sm-6">
              <FormControl fullWidth>
                <InputLabel>Day Type</InputLabel>
                <Select
                  value={dayType}
                  size={isMobile ? "small" : "medium"}
                  label="Day Type"
                  onChange={(e) => setDayType(e.target.value)}
                >
                  <MenuItem value="Single Day">Single Day</MenuItem>
                  <MenuItem value="Multiple Days">Multiple Days</MenuItem>
                </Select>
              </FormControl>
            </div>

            {/* Single Day */}
            {dayType === "Single Day" && (
              <>
                <div className="col-sm-6">
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      value={dayDuration}
                      onChange={(e) => setDayDuration(e.target.value)}
                    >
                      <FormControlLabel
                        value="Full Day"
                        control={<Radio />}
                        label="Full Day"
                      />
                      <FormControlLabel
                        value="Half Day"
                        control={<Radio />}
                        label="Half Day"
                      />
                    </RadioGroup>
                  </FormControl>
                </div>

                <div className="col-sm-6">
                  <DatePicker
                    value={singleDay}
                    onChange={setSingleDay}
                    format="DD MMM, YYYY"
                    placeholder="Select Date *"
                    className="rmdp-input"
                    required
                    minDate={new Date()}
                    mapDays={mapDays}
                    editable={false}
                  />
                </div>
              </>
            )}

            {/* Multiple Days */}
            {dayType === "Multiple Days" && (
              <div className="col-sm-12">
                <DatePicker
                  value={multipleDays}
                  onChange={setMultipleDays}
                  multiple
                  format="DD MMM, YYYY"
                  placeholder="Select Dates *"
                  className="rmdp-input"
                  sort
                  minDate={new Date()}
                  mapDays={mapDays}
                  editable={false}
                />
              </div>
            )}

            {/* Lunch & Late Pickup */}
            <div className="col-sm-12">
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeLunch}
                      onChange={(e) => setIncludeLunch(e.target.checked)}
                    />
                  }
                  label="Include Lunch (+$10 per day)"
                />
              </FormGroup>

              {type?.toLowerCase() === "sherman-oaks" && (
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeLatePickup}
                        onChange={(e) => setIncludeLatePickup(e.target.checked)}
                      />
                    }
                    label="Add-on Late Pick-up Fee ($15.00 till 5:30 PM)"
                  />
                </FormGroup>
              )}
            </div>

            {/* COUPON CODE FIELD - Only visible if NO promo code applied */}
            {!urlHasPromo && (
              <div className="col-sm-12">
                <TextField
                  label="Coupon Code"
                  value={couponCode}
                  onChange={(e) => {
                    setCouponCode(e.target.value);
                    if (couponError) setCouponError(""); // Clear error on type
                  }}
                  onBlur={handleCouponBlur}
                  disabled={couponUsedByExistingUser} // Disable if API says already used
                  size={isMobile ? "small" : "medium"}
                  fullWidth
                  error={!!couponError || couponUsedByExistingUser}
                  helperText={
                    couponError ||
                    (couponUsedByExistingUser
                      ? "The coupon code is already used by this user."
                      : isCouponApplied
                        ? "Coupon Applied: $50 OFF"
                        : "")
                  }
                  InputProps={{
                    style: {
                      color: isCouponApplied ? "green" : "inherit",
                    },
                  }}
                />
              </div>
            )}

            {/* Stripe Card */}
            <div className="col-sm-12 mt-2">
              <Typography variant="subtitle1">Enter Card Details</Typography>
              <div
                style={{
                  padding: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                  marginTop: "8px",
                }}
              >
                <CardElement
                  options={{
                    hidePostalCode: true,
                    style: {
                      base: {
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: "500",
                        fontSize: "16px",
                        color: "#333",
                        "::placeholder": {
                          color: "#999",
                        },
                      },
                      invalid: {
                        color: "#fa755a",
                      },
                    },
                  }}
                />
              </div>
            </div>

            {/* Submit */}
            <div className="col-sm-12 mt-3">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={loading}
              >
                {loading ? "Processing..." : "Book & Pay"}
              </Button>
            </div>
          </div>
        </form>
      </div >



      {/* Promo Already Used Dialog (ONLY when url promo is present) */}
      < Dialog
        open={promoAlreadyUsed}
        onClose={() => setPromoAlreadyUsed(false)}
      >
        <DialogTitle>Promo Already Used</DialogTitle>
        <DialogContent>
          <Typography>
            A promotional discount has already been used with this email. The
            current booking will proceed without additional discounts.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPromoAlreadyUsed(false)}
            variant="contained"
          >
            Okay
          </Button>
        </DialogActions>
      </Dialog >
    </div >
  );
};

export default BookingForm;
